import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, MessageCircle, Send } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const SECTIONS = [
  {
    title: "Combo Sets",
    pages: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/fa45c4420709eb08_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_03.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/10d72181966c6f57_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_04.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/83bb2ec36e8b66b1_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_05.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/38db741d902e1e23_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_06.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/ec69e7a246a4a8b1_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_07.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/23a04c246aef06aa_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_09.webp",
    ],
  },
  {
    title: "Make Your Combo",
    pages: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/764c313dae629e74_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_10.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/05ea31f8a06981e2_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_11.webp",
    ],
  },
  {
    title: "Plastic Pens",
    pages: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/a6083ad258b323d9_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_28.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/815b5d50bf9f65ab_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_29.webp",
    ],
  },
  {
    title: "Metal Pens",
    pages: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/2896d18c13b851c2_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_30.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/f42be8aefdaff011_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_31.webp",
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/c3b97c0d031355b6_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_32.webp",
    ],
  },
  {
    title: "Hand Bags & Jute Bags",
    pages: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/c7488e10751da7d8_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_12.webp",
    ],
  },
  {
    title: "Bags, Boxes & Folders",
    pages: [
      "https://customer-assets-7cd3h4nn.emergentagent.net/job_pro-print-solution/artifacts/bb6c7b88a96e292e_Master%20Catalogue%20Cost%20Sept%202026%20%281%29_Page_13.webp",
    ],
  },
];

const ORDER_STEPS = [
  ["01", "Pick your sets", "Browse the pages and note the Sr numbers you like."],
  ["02", "Send Sr + quantity", "WhatsApp us the list with your quantity and logo file."],
  ["03", "We quote & deliver", "Branding, GST and delivery quoted, then printed and shipped."],
];

export default function Catalog() {
  const [sec, setSec] = useState(0);
  const [idx, setIdx] = useState(0);
  const pages = SECTIONS[sec].pages;

  const switchSec = (i) => {
    setSec(i);
    setIdx(0);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, pages.length - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pages.length]);

  const navBtn =
    "inline-flex items-center gap-1.5 border border-edge bg-surface text-mist font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-sm hover:text-cyanbrand hover:border-cyanbrand transition-colors disabled:opacity-30 disabled:pointer-events-none";

  return (
    <div className="grain min-h-screen bg-ink text-paper font-body" data-testid="catalog-page">
      <header className="sticky top-0 z-40 bg-ink/85 backdrop-blur-xl border-b border-edge">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="catalog-logo-link" className="flex items-center gap-3">
            <LogoMark />
            <span className="leading-none">
              <span className="block font-display font-semibold tracking-tight text-paper">Catalog</span>
              <span className="block font-mono text-[10px] tracking-[0.35em] text-mist mt-1">GIFTING &amp; COMBOS</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="https://wa.me/917021504681"
              target="_blank"
              rel="noreferrer"
              data-testid="catalog-whatsapp-link"
              className="hidden sm:inline-flex items-center gap-2 border border-edge text-paper font-mono text-[11px] tracking-[0.15em] px-4 py-2.5 rounded-sm hover:border-cyanbrand hover:text-cyanbrand transition-colors"
            >
              <MessageCircle size={14} /> WhatsApp Order
            </a>
            <Link
              to="/"
              data-testid="catalog-home-link"
              className="inline-flex items-center gap-2 bg-paper text-ink font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-cyanbrand transition-colors"
            >
              Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyanbrand">/ Catalog</span>
          <h1 className="mt-4 font-display font-semibold tracking-tight text-4xl sm:text-5xl text-paper">
            Gifting Catalog.
          </h1>
          <p className="mt-3 font-mono text-xs text-yel tracking-wide">
            Branding, GST &amp; Delivery Extra on all Prices
          </p>
          <p className="mt-4 max-w-xl text-sm text-mist leading-relaxed">
            Ready-made corporate gift combos and add-ons — pens, notebooks, bottles, bags,
            boxes and more. Note the <span className="text-paper font-semibold">Sr / item code</span> of
            what you like, then send it to us with your quantity and logo.
          </p>
        </motion.div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="catalog-section-tabs">
          {SECTIONS.map((s, i) => (
            <button
              key={s.title}
              data-testid={`catalog-tab-${i}`}
              onClick={() => switchSec(i)}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-sm border transition-colors ${
                i === sec
                  ? "bg-cyanbrand text-ink border-cyanbrand font-semibold"
                  : "border-edge text-mist hover:text-paper hover:border-mist"
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-6 border border-edge bg-surface rounded-sm p-3 sm:p-5"
        >
          <img
            key={`${sec}-${idx}`}
            src={pages[idx]}
            alt={`${SECTIONS[sec].title} catalog page ${idx + 1} — prices per quantity`}
            data-testid="catalog-page-image"
            className="w-full rounded-sm"
          />

          <div className="sticky top-[84px] z-10 -mx-1 mt-4 flex items-center justify-between gap-2 rounded-sm bg-surface/95 px-1 py-2 backdrop-blur">
            <button
              data-testid="catalog-prev"
              onClick={() => setIdx(Math.max(idx - 1, 0))}
              disabled={idx === 0}
              className={navBtn}
            >
              <ChevronLeft size={15} /> Prev
            </button>
            <span className="font-mono text-[11px] tracking-[0.25em] text-mist" data-testid="catalog-page-indicator">
              {SECTIONS[sec].title.toUpperCase()} · PAGE {idx + 1} / {pages.length}
            </span>
            <button
              data-testid="catalog-next"
              onClick={() => setIdx(Math.min(idx + 1, pages.length - 1))}
              disabled={idx === pages.length - 1}
              className={navBtn}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>

          <div className={`mt-4 gap-2 ${pages.length > 1 ? "grid grid-cols-6" : "hidden"}`}>
            {pages.map((p, i) => (
              <button
                key={p}
                data-testid={`catalog-thumb-${i}`}
                onClick={() => setIdx(i)}
                aria-label={`Go to ${SECTIONS[sec].title} page ${i + 1}`}
                className={`overflow-hidden rounded-sm border transition-colors ${
                  i === idx ? "border-cyanbrand" : "border-edge hover:border-mist"
                }`}
              >
                <img src={p} alt="" className="w-full aspect-[3/4] object-cover object-top" loading="lazy" />
              </button>
            ))}
          </div>
          <p className="mt-3 font-mono text-[10px] text-mist/60 tracking-wide text-center">
            Use ← → arrow keys to flip pages · open the image in a new tab to zoom
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {ORDER_STEPS.map(([num, title, desc], i) => (
            <motion.div
              key={num}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="border border-edge bg-surface rounded-sm p-5"
              data-testid={`catalog-order-step-${i}`}
            >
              <span className="font-mono text-2xl font-semibold" style={{ WebkitTextStroke: "1px #00E5FF", color: "transparent" }}>
                {num}
              </span>
              <h3 className="mt-3 font-display font-medium text-lg text-paper">{title}</h3>
              <p className="mt-2 text-sm text-mist leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 border-t border-edge pt-8 flex flex-wrap items-center gap-4">
          <a
            href="https://wa.me/917021504681"
            target="_blank"
            rel="noreferrer"
            data-testid="catalog-order-whatsapp"
            className="inline-flex items-center gap-2 bg-cyanbrand text-ink font-bold text-sm px-6 py-3.5 rounded-sm hover:bg-paper transition-colors"
          >
            <MessageCircle size={15} /> Order on WhatsApp
          </a>
          <Link
            to="/"
            data-testid="catalog-order-enquiry"
            className="inline-flex items-center gap-2 border border-edge text-paper font-mono text-xs tracking-[0.15em] px-6 py-3.5 rounded-sm hover:border-cyanbrand hover:text-cyanbrand transition-colors"
          >
            <Send size={14} /> Send Enquiry Form
          </Link>
          <p className="font-mono text-[11px] text-mist/70 w-full sm:w-auto">
            Prices exclusive of branding, GST &amp; delivery · Rates as per qty slabs / MOQ shown
          </p>
        </div>
      </main>
    </div>
  );
}
